import { useMemo } from 'react';

import { type TFlatBlock } from './helpers/blocksToFlatDocument';
import { getMaxDepth, getMinDepth } from './helpers/getDepthRange';
import removeBlockChildren from './helpers/removeBlockChildren';
import { type TBlockStatus } from './useBlockState';

type TProps = {
  items: TFlatBlock[];
  active: null | TBlockStatus;
  overBlockStatus: null | {
    id: string;
    position: 'before' | 'after';
  };
  offset: number;
};

export default function useProjectedDepth({ items, active, overBlockStatus, offset }: TProps) {
  return useMemo(() => {
    if (active === null || overBlockStatus === null) {
      return 0;
    }

    const bItems = removeBlockChildren(items, overBlockStatus.id);
    const overIndex = bItems.findIndex((k) => k.id === overBlockStatus.id);
    let minDepth = 0;
    let maxDepth = 0;
    if (active.id === overBlockStatus.id) {
      minDepth = getMinDepth(bItems, overIndex + 1);
      maxDepth = getMaxDepth(bItems, overIndex);
    } else if (overBlockStatus.position === 'before') {
      maxDepth = getMaxDepth(bItems, overIndex);
      minDepth = getMinDepth(bItems, overIndex);
    } else if (overBlockStatus.position === 'after') {
      maxDepth = getMaxDepth(bItems, overIndex + 1);
      minDepth = getMinDepth(bItems, overIndex + 1);
    }
    const projectedDepth = active.flatBlock.depth + offset;

    if (projectedDepth >= maxDepth) {
      return maxDepth;
    } else if (projectedDepth < minDepth) {
      return minDepth;
    }
    return projectedDepth;
  }, [items, active, overBlockStatus, offset]);
}
